import { LocationOnOutlined } from '@mui/icons-material'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { Box, Typography, Card, CircularProgress } from '@mui/material'
import { useEffect, useState, useMemo } from 'react'
import type { AnnualPlanTeamMember } from '../../../models/AnnualPlanTeamMember'
import { BlueButton } from '../../customComponents/buttons/BlueButton'
import { EventDisplay } from '../../customComponents/EventDisplay'
import { IconWithLabel } from '../../customComponents/IconWithLabel'
import { BasicTable, type BasicTableColumn } from './priorityActivities/BasicTable'
import { UserDetailsView } from '../../customComponents/user/DetailsView'
import { AddMeetingModal } from './AddMeetingModal'
import { ThingsToConsiderButton } from './priorityActivities/ThingsToConsiderButton'
import type { AnnualPlanMeeting } from '../../../models/AnnualPlanMeeting'
import { toast } from 'react-toastify'
import { GetAnnualPlanTeam } from '../../../services/AnnualPlanService'
import { DeleteMeeting, GetAnnualPlanMeetings } from '../../../services/MeetingScheduleService'
import { PlanOrganizationTaskName, PlanOrganizationTaskStatus } from '../../../models/PlanOrganizationTask'
import { Modal } from '../../customComponents/Modal'
import type { Address } from '../../../models/Address'
import VideocamIcon from '@mui/icons-material/Videocam'
import { TrackMeetingModal } from './TrackMeetingModal'
import { useModalState } from '../../../hooks/use-modalState'
import { handleApiResponse } from '../../../utils/apiHelpers'

interface Props {
  annualPlanId: number
  readOnly: boolean
  rcnId: number
  showComments?: boolean
  status: string
  color?: string
  showTableOnly?: boolean
  isImplementationPhase?: boolean
  trackingReadOnly?: boolean
  budgetOverviewView?: boolean
}

export function MeetingSchedules (props: Props): JSX.Element {
  // Primary modal state management (handles locking/unlocking automatically)
  const { modalState, startModal, currentItem, handleCloseModal } = useModalState<AnnualPlanMeeting>(PlanOrganizationTaskName.MeetingSchedule)

  // Other component state
  const [teamMembers, setTeamMembers] = useState<AnnualPlanTeamMember[]>([])
  const [meetings, setMeetings] = useState<AnnualPlanMeeting[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const isImplementationPhase = props.isImplementationPhase ?? false

  // Table configuration
  const columns: BasicTableColumn[] = [
    { id: 'dateDisplay', label: 'Date', minWidth: 180 },
    { id: 'title', label: 'Title', minWidth: 200 },
    { id: 'description', label: 'Description', minWidth: 250 },
    { id: 'displayBudget', label: 'Budget', minWidth: 120 }
  ]

  const fetchTeam = async (): Promise<void> => {
    const team = await handleApiResponse(GetAnnualPlanTeam(props.annualPlanId))
    if (team != null) setTeamMembers(team)
  }

  const fetchMeetings = async (updateLoadingState = true): Promise<void> => {
    if (updateLoadingState) setIsLoading(true)
    const meetings = await handleApiResponse(GetAnnualPlanMeetings(props.annualPlanId, props.rcnId, !isImplementationPhase))
    if (meetings != null) setMeetings(meetings.sort((a, b) => new Date(a.meetingDate).getTime() - new Date(b.meetingDate).getTime()))
    if (updateLoadingState) setIsLoading(false)
  }

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (props.annualPlanId === 0 || props.rcnId === 0) return
      await Promise.all([
        fetchTeam(),
        fetchMeetings(false)
      ])
      setIsLoading(false)
    }

    void fetchData()
  }, [props.annualPlanId, props.rcnId])

  const addMeetingClick = (): void => {
    if (props.status === PlanOrganizationTaskStatus.NotStarted) {
      toast.error('Set the status to "In Progress" in order to continue.')
      return
    }

    void startModal('add')
  }

  // Unified success handler
  const onSuccess = async (): Promise<void> => {
    await fetchMeetings()

    switch (modalState) {
      case 'update':
        toast.success('Meeting Data Updated.')
        break
      case 'add':
        toast.success('Meeting Data Saved.')
        break
      case 'track':
        toast.success('Meeting Data Tracked.')
        break
      case 'delete':
        toast.success('Meeting deleted.')
        break
    }

    await handleCloseModal() // This handles unlocking automatically
  }

  const continueDeleteMeeting = async (): Promise<void> => {
    if (currentItem?.id != null) {
      await DeleteMeeting(currentItem.id, currentItem.isUpdate ?? false)
    }
    await onSuccess()
  }

  // Helper function for rendering location in expanded content
  const renderLocation = (meeting: AnnualPlanMeeting): JSX.Element => {
    if (meeting.isVirtual && meeting.virtualLink !== '') {
      return (
        <IconWithLabel
          labelAsLink
          icon={<VideocamIcon />}
          labelText={meeting.virtualLink}
        />
      )
    }

    if (meeting.address.street !== '') {
      return (
        <IconWithLabel
          icon={<LocationOnOutlined />}
          whiteSpace='normal'
          labelText={stringFromAddress(meeting.address)}
        />
      )
    }

    return <Typography variant='body2'>No location specified</Typography>
  }

  // Helper function to create expanded content for each meeting row
  const expandedContent = (meeting: AnnualPlanMeeting): JSX.Element | undefined => {
    // Only show expanded content if there are tracking details or in implementation phase
    if (!isImplementationPhase && meeting.actualDollarAmount == null && meeting.wasCompleted == null) {
      return undefined
    }

    return (
      <Box sx={{ p: '2rem', backgroundColor: 'var(--grey-100)' }}>
        {/* Location and contact info */}
        <Box sx={{ display: 'flex', gap: '2rem', mb: '1rem' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant='caption' sx={{ fontWeight: 600 }}>LOCATION</Typography>
            {renderLocation(meeting)}
          </Box>
          {meeting.primaryContact !== null && (
            <Box sx={{ flex: 1 }}>
              <Typography variant='caption' sx={{ fontWeight: 600 }}>PRIMARY CONTACT</Typography>
              <UserDetailsView user={meeting.primaryContact} allowEdit={false} />
            </Box>
          )}
        </Box>

        {/* Tracking details if present */}
        {(meeting.actualDollarAmount != null || meeting.wasCompleted != null) && (
          <Box sx={{ mt: '1rem' }}>
            <Typography variant='caption' sx={{ fontWeight: 600, mb: '.5rem', display: 'block' }}>TRACKING DETAILS</Typography>
            <Box sx={{ display: 'flex', gap: '2rem' }}>
              {meeting.wasCompleted != null && (
                <Typography variant='body1'>
                  Meeting Was Held: <strong>{meeting.wasCompleted ? 'Yes' : 'No'}</strong>
                </Typography>
              )}
              {meeting.actualDollarAmount != null && (
                <Typography variant='body1'>
                  Actual Budget: <strong>${meeting.actualDollarAmount.toLocaleString()}</strong>
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>
    )
  }

  interface MeetingScheduleRow extends AnnualPlanMeeting {
    dateDisplay: JSX.Element
    displayBudget: string
  }

  const createTableRows = (meetings: AnnualPlanMeeting[]): MeetingScheduleRow[] => {
    return meetings.map(meeting => {
      const budget = meeting.adjustedDollarAmount ?? meeting.dollarAmount ?? 0

      return {
        ...meeting,
        dateDisplay: (
          <EventDisplay
            eventBoxStyle={{ width: '2.25em', height: '3.375em', my: '.5em' }}
            description={meeting.description}
            budgetRequestAmount={meeting.adjustedDollarAmount ?? meeting.dollarAmount}
            actualBudgetAmount={meeting.actualDollarAmount}
            event={{
              title: meeting.title,
              date: meeting.meetingDate,
              startTime: meeting.meetingDate,
              endTime: meeting.meetingEndDate ?? undefined
            }}
          />
        ),
        displayBudget: `$${budget.toLocaleString()}`,
        isUpdated: meeting.isUpdate,
        isTracked: meeting.isUpdate && meeting.wasCompleted != null,
        expandedContent: expandedContent(meeting)
      }
    })
  }

  const stringFromAddress = (adr: Address): string => {
    if (adr.city === '') {
      return adr.street
    }

    const addressString = `${adr.street}${adr.apartment == null ? '' : ` ${adr.apartment}`} ${adr.city}, ${adr.state} ${adr.zip}`
    return addressString
  }

  // Memoize rows to avoid recreating on every render
  const rows = useMemo(() => createTableRows(meetings), [meetings])

  return <Box display='flex' flexDirection='column' gap='1em' data-testid='meetingSchedules'>
    <Typography variant='h4' color={props.color}>Meetings</Typography>

    {props.showTableOnly !== true && (
      <Box sx={{ width: '100%', alignItems: 'center' }}>
        {!props.readOnly &&
          <BlueButton onClick={addMeetingClick} dataTestId='addMeetingButton'>
            Add Meeting <AddCircleIcon sx={{ ml: '0.5em' }} />
          </BlueButton>
    }

        <Box sx={{ float: 'right' }}>
          <ThingsToConsiderButton
            buttonDataTestId='rcnOperationsThingsToConsiderButton'
            thingsToConsiderSections={[]}
            cardDataTestId='rcnOperationsThingsToConsiderCard'
          />
        </Box>
      </Box>
    )}

    <Card data-testid='cardContainer' sx={{ minHeight: '400px', width: '100%', display: meetings.length === 0 ? 'grid' : 'block' }}>
      {isLoading && <CircularProgress />}

      {!isLoading && meetings.length === 0 &&
        <Box sx={{ justifySelf: 'center', alignSelf: 'center', width: '300px' }}>
          <Box sx={{ justifySelf: 'center', mb: '.75em' }}>
            <CalendarMonthIcon sx={{ color: 'var(--blue-300)', width: '150px', height: '150px' }} />
            <Typography variant='h4'>No Meetings Added</Typography>
          </Box>
          {!props.readOnly &&
            <Typography variant='body1' sx={{ fontSize: '16px' }}>Use the Add Meeting button to add a Meeting to the schedule.</Typography>}
        </Box>
      }

      {!isLoading && meetings.length > 0 &&
        <BasicTable
          columns={columns}
          isLoading={isLoading}
          rows={rows}
          onEdit={(meeting) => { void startModal('update', meeting) }}
          onDelete={(meeting) => { void startModal('delete', meeting) }}
          onUpdate={(meeting) => { void startModal('update', meeting) }}
          onTrack={(meeting) => { void startModal('track', meeting) }}
          implementationPhase={isImplementationPhase}
          dataTestId='meetingSchedulesTable'
          indexRowIds={true}
          readonly={props.readOnly}
          trackingReadonly={props.trackingReadOnly}
          budgetOverviewView={props.budgetOverviewView}
        />
      }

    </Card>

    {modalState === 'delete' && currentItem != null &&
      <Modal
        title='Are you sure?'
        onClose={handleCloseModal}
        open={true}
        onConfirm={continueDeleteMeeting}
        bodyContent='You are about to delete this item.  This action will prompt a review from the START team.  Are you sure you wish to continue?'
        confirmButtonText='Yes'
        cancelButtonText='No'
      />
    }

    {(modalState === 'add' || modalState === 'update') &&
      <AddMeetingModal
        teamMembers={teamMembers}
        meeting={currentItem}
        isImplementationPhase={isImplementationPhase}
        onCancel={handleCloseModal}
        onSuccess={onSuccess}
        orgId={props.rcnId}
        annualPlanId={props.annualPlanId}
      />
    }

    {modalState === 'track' && currentItem != null &&
      <TrackMeetingModal
        meeting={currentItem}
        onSuccess={onSuccess}
        onCancel={handleCloseModal}
      />
    }
  </Box>
}
